import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

import { usePromoCode } from "frontend/context/CheckoutContext";
import Heading from "core/Heading";
import BodyText from "core/BodyText";
import colors from "core/colors";
import OrderSummaryItem from "./OrderSummaryItem";
import {
    StyledOrderSummary,
    UsdBodyText,
    TotalCharge,
    SubsOrderItem
} from "./styles";
import { TEAM_MONTHLY_PLAN, TEAM_YEARLY_PLAN } from "subscriptions/data";
import { PRO_ANNUAL_DOWNLOAD, PRO_MONTHLY_DOWNLOAD } from "icons/helpers";

import {
    YEARLY_SUBSCRIPTION_PLAN_NAME,
    MONTHLY_SUBSCRIPTION_PLAN_NAME,
    CENTS,
    PERCENT
} from "core/constants";
import { truncateToDecimals } from "frontend/components/helpers";

const OrderSummaryBody = ({
    orderSummaryTextLine1,
    orderSummaryTextLine2,
    thumbnail,
    itemTitle,
    className,
    itemPrice,
    itemPriceOrderSummaryItem,
    showOrderSummary = true,
    licenseSelection = ""
}) => {
    const {
        promoCodeDetails: { discountType, amountOff }
    } = usePromoCode();

    const createPromoCodeMessaging = () => {
        let promoCodeMessaging = "";

        if (discountType === CENTS) {
            promoCodeMessaging = `$${amountOff} off applied`;
        } else if (discountType === PERCENT) {
            promoCodeMessaging = `${amountOff * 100}% off applied`;
        }

        return promoCodeMessaging;
    };

    const checkoutTotalTitle = "Charged today";
    const checkoutTotalDollarType = "USD";
    const isAnnualSub =
        licenseSelection === PRO_ANNUAL_DOWNLOAD ||
        licenseSelection === TEAM_YEARLY_PLAN;
    const isEitherAnnualOrMonthlySub =
        licenseSelection === PRO_ANNUAL_DOWNLOAD ||
        licenseSelection === PRO_MONTHLY_DOWNLOAD ||
        licenseSelection === TEAM_YEARLY_PLAN ||
        licenseSelection === TEAM_MONTHLY_PLAN;
    const yearlyChargeText = checkoutTotalTitle + " (1 year at $3.33/month)";
    const yearlyChargeTextTeam =
        checkoutTotalTitle + " (1 year at $3.33/month per user)";

    const chargedToday = isEitherAnnualOrMonthlySub
        ? truncateToDecimals(itemPrice)
        : itemPrice;

    const itemPriceDollars = chargedToday.toString().split(".")[0];
    const itemPriceCents = chargedToday.toString().split(".")[1];

    const checkoutChargeTitle = ({ planType }) => {
        switch (planType) {
            case TEAM_YEARLY_PLAN:
                return yearlyChargeTextTeam;
            case PRO_ANNUAL_DOWNLOAD:
                return yearlyChargeText;
            default:
                return checkoutTotalTitle;
        }
    };

    return (
        <StyledOrderSummary
            className={className}
            data-testid="checkout-order-total"
        >
            {showOrderSummary && (
                <>
                    <Heading
                        type="h2"
                        size="xs"
                        headingColor={colors.secondary.orca}
                    >
                        Order Item
                    </Heading>
                    {isEitherAnnualOrMonthlySub ? (
                        <SubsOrderItem tabIndex={0}>
                            {isAnnualSub
                                ? YEARLY_SUBSCRIPTION_PLAN_NAME
                                : MONTHLY_SUBSCRIPTION_PLAN_NAME}
                        </SubsOrderItem>
                    ) : (
                        <OrderSummaryItem
                            className={className}
                            thumbnail={thumbnail}
                            itemTitle={itemTitle}
                            itemPriceOrderSummaryItem={
                                itemPriceOrderSummaryItem
                            }
                            orderSummaryTextLine1={orderSummaryTextLine1}
                            orderSummaryTextLine2={orderSummaryTextLine2}
                        />
                    )}
                </>
            )}

            <TotalCharge
                justifyContent="space-between"
                alignItems="baseline"
                showOrderSummary={showOrderSummary}
                tabIndex={0}
                aria-label={`${checkoutTotalTitle} ${itemPriceDollars} dollars and ${itemPriceCents} cents in ${checkoutTotalDollarType}`}
            >
                <div>
                    <BodyText
                        type="p"
                        size="sm"
                        copyColor={colors.secondary.orca}
                    >
                        {checkoutChargeTitle({ planType: licenseSelection })}
                    </BodyText>

                    <BodyText
                        type="p"
                        size="sm"
                        copyColor={colors.secondary.orca}
                    >
                        {createPromoCodeMessaging()}
                    </BodyText>
                </div>

                <Heading
                    type="p"
                    size="xs"
                    headingColor={colors.secondary.orca}
                    alignment="right"
                >
                    <UsdBodyText
                        type="span"
                        size="sm"
                        copyColor={colors.secondary.oolong}
                    >
                        {checkoutTotalDollarType}
                    </UsdBodyText>
                    ${chargedToday}
                </Heading>
            </TotalCharge>
        </StyledOrderSummary>
    );
};

const OrderSummary = styled(OrderSummaryBody)``;

OrderSummary.propTypes = {
    className: PropTypes.string,
    thumbnail: PropTypes.string,
    itemTitle: PropTypes.string,
    itemPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    orderSummaryTextLine1: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    orderSummaryTextLine2: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    showOrderSummary: PropTypes.bool.isRequired,
    licenseSelection: PropTypes.string
};

export default OrderSummary;
